import React, { useCallback, useEffect, useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  TransformStats,
  Creature,
  Summon,
  PairedUGN,
  UpgradableNumber,
} from "@dndModels";
import { AbilityScoreType, SpellLevel } from '@dndModels/Types';
import UpgradableNumberBox from '@dndComponents/UpgradableNumberBox';
import LoadingBox from '@components/input/LoadingBox';
import { ABILITY_SCORES } from '@dndModels/Constants';

interface Props {
  obj?: Creature | Summon;
  ability_scores: TransformStats;
  slot_level?: SpellLevel;
  use_creature: boolean;
  onChange: (changed: TransformStats) => void;
  disabled?: boolean;
}

export default function PairedStatsUGNInput({ 
  obj,
  ability_scores,
  slot_level,
  use_creature,
  onChange,
  disabled = false 
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [use_ability_scores, setUseAbilityScores] = useState(new TransformStats());

  const load = useCallback(() => {
    setUseAbilityScores(ability_scores.clone());
    setLoading(false);
  }, [ability_scores]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);

  const renderPairedUGNBox = (ability: string, target: boolean) => {
    let ability_score: PairedUGN = new PairedUGN();
    switch(ability) {
      case "Strength":
        ability_score = use_ability_scores.strength;
      break;
      case "Dexterity":
        ability_score = use_ability_scores.dexterity;
      break;
      case "Constitution":
        ability_score = use_ability_scores.constitution;
      break;
      case "Intelligence":
        ability_score = use_ability_scores.intelligence;
      break;
      case "Wisdom":
        ability_score = use_ability_scores.wisdom;
      break;
      case "Charisma":
        ability_score = use_ability_scores.charisma;
      break;
    }

    return (
      <UpgradableNumberBox
        name={ability}
        slot_level={slot_level}
        value={ target ? ability_score.from_target : ability_score.from_creature } 
        disabled={disabled}
        quick_options={ABILITY_SCORES as AbilityScoreType[]}
        onChange={(changed: UpgradableNumber) => {
          switch(ability) {
            case "Strength":
              if (target) {
                use_ability_scores.strength.from_target = changed.clone();
              } else {
                use_ability_scores.strength.from_creature = changed.clone();
              }
            break;
            case "Dexterity":
              if (target) {
                use_ability_scores.dexterity.from_target = changed.clone();
              } else {
                use_ability_scores.dexterity.from_creature = changed.clone();
              }
            break;
            case "Constitution":
              if (target) {
                use_ability_scores.constitution.from_target = changed.clone();
              } else {
                use_ability_scores.constitution.from_creature = changed.clone();
              }
            break;
            case "Intelligence":
              if (target) {
                use_ability_scores.intelligence.from_target = changed.clone();
              } else {
                use_ability_scores.intelligence.from_creature = changed.clone();
              }
            break;
            case "Wisdom":
              if (target) {
                use_ability_scores.wisdom.from_target = changed.clone();
              } else {
                use_ability_scores.wisdom.from_creature = changed.clone();
              }
            break;
            case "Charisma":
              if (target) {
                use_ability_scores.charisma.from_target = changed.clone();
              } else {
                use_ability_scores.charisma.from_creature = changed.clone();
              }
            break;
          }
          onChange(use_ability_scores);
          setPing(!ping);
        }}
      />
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <Grid container spacing={0} direction="row">
      <Grid size={12} className="header centered">
        Ability Scores
      </Grid>
      <Grid size={12}>
        <Grid container spacing={0} direction="row">
          <Grid size={12} className="header centered">From Target</Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Strength", true) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Dexterity", true) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Constitution", true) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Intelligence", true) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Wisdom", true) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Charisma", true) }
          </Grid>
        </Grid>
      </Grid>
      { use_creature &&
        <Grid size={12}>
        <Grid container spacing={0} direction="row">
          <Grid size={12} className="header centered">From Creature</Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Strength", false) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Dexterity", false) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Constitution", false) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Intelligence", false) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Wisdom", false) }
          </Grid>
          <Grid size={4}>
            { renderPairedUGNBox("Charisma", false) }
          </Grid>
        </Grid>
        </Grid>
      }
    </Grid>
  );
}
